import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft, faChevronRight, faXmark, faImage
} from '@fortawesome/free-solid-svg-icons';
import Spinner from 'src/components/shared/Spinner';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
`;

const Control = styled.button`
  position: absolute;
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 28px;
  padding: 16px;
`;

const Close = styled(Control)`
  top: 8px;
  right: 8px;
`;

const Prev = styled(Control)`
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
`;

const Next = styled(Control)`
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
`;

const Loading = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const ImageLightbox = ({ open, images, index, alt, onIndexChange, onClose, onImageError }) => {
  const count = images?.length || 0;
  const go = step => onIndexChange((index + step + count) % count);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(new Set());

  useEffect(() => { setLoaded(false); }, [index, open]);
  useEffect(() => { setErrored(new Set()); }, [open]);

  const handleError = event => {
    setLoaded(true);
    setErrored(prev => new Set(prev).add(index));
    if (onImageError) onImageError(event);
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = event => {
      if (event.key === 'ArrowRight') onIndexChange((index + 1 + count) % count);
      else if (event.key === 'ArrowLeft') onIndexChange((index - 1 + count) % count);
      else if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, index, count, onIndexChange, onClose]);

  if (!open || !count) return null;

  return (
    <Backdrop data-testid="lightbox-backdrop" onClick={onClose}>
      {errored.has(index) ? (
        <Loading data-testid="lightbox-placeholder">
          <FontAwesomeIcon icon={faImage} size="2xl" style={{ color: '#fff' }} />
        </Loading>
      ) : (
        <>
          <Image
            src={images[index]}
            alt={alt || ''}
            onClick={event => event.stopPropagation()}
            onLoad={() => setLoaded(true)}
            onError={handleError}
          />
          {!loaded && (
            <Loading data-testid="lightbox-loading">
              <Spinner $size="40px" $track="rgba(255, 255, 255, 0.3)" $accent="#fff" />
            </Loading>
          )}
        </>
      )}
      {count > 1 && (
        <>
          <Prev
            aria-label="Previous image"
            onClick={event => { event.stopPropagation(); go(-1); }}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Prev>
          <Next
            aria-label="Next image"
            onClick={event => { event.stopPropagation(); go(1); }}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Next>
        </>
      )}
      <Close
        aria-label="Close"
        onClick={event => { event.stopPropagation(); onClose(); }}
      >
        <FontAwesomeIcon icon={faXmark} />
      </Close>
    </Backdrop>
  );
};

export default ImageLightbox;
